import { useState, useEffect, createContext, useContext, Suspense, lazy } from "react";
import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { formatApiError } from "./utils/errorFormatter";
import { clearBrowserPhi } from "./utils/browserPhi";
import { Toaster } from "@/components/ui/sonner";
import { TruthAckDialog } from "./components/truth/TruthAckDialog";
import { installTruthAckInterceptor } from "./components/truth/truthAck";
import { FavoritesProvider } from "./contexts/FavoritesContext";
import { DomainProvider } from "./contexts/DomainContext";
import SessionWarningDialog from "./components/SessionWarningDialog";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import { Loader2 } from "lucide-react";

// P3 Cookie Migration: send the HttpOnly `session_token` cookie on every
// request. Removes the last XSS-token-theft foothold (no localStorage token).
axios.defaults.withCredentials = true;

// ========================================================================
// 2026-02-25 PRODUCTION CROSS-ORIGIN COOKIE FIX
// ========================================================================
// The frontend build has `REACT_APP_BACKEND_URL` baked in at compile time
// (e.g. `https://goodwinguard.com`). When the same build is served from a
// DIFFERENT custom domain (e.g. `careflow-safety.emergent.host`), every
// XHR becomes cross-origin. Modern browsers (Chrome 130+, Safari, Firefox)
// BLOCK third-party cookies by default → login succeeds, but the
// `Set-Cookie` for `goodwinguard.com` is silently dropped → the very next
// `/auth/me` returns 401 → dashboard fires "Failed to load dashboard
// data" → user bounces to /login. The exact production bug users reported.
//
// Fix: at runtime, detect the cross-origin case and rewrite every
// outgoing request URL that targets the configured BACKEND_URL to a
// RELATIVE path. That keeps cookies strictly first-party regardless of
// which domain the user visits. Same-origin deployments (preview,
// goodwinguard.com) are UNAFFECTED — the check short-circuits.
//
// We patch BOTH axios (used by ~80% of the app) AND window.fetch (used by
// the remaining hooks/services) so a page that imports
// `process.env.REACT_APP_BACKEND_URL` directly still gets the fix without
// any file-by-file edits.
const _CONFIGURED_BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const _CROSS_ORIGIN_BACKEND = (() => {
  if (!_CONFIGURED_BACKEND || typeof window === "undefined") return null;
  try {
    const backendOrigin = new URL(_CONFIGURED_BACKEND).origin;
    if (backendOrigin !== window.location.origin) {
      console.info(
        `[HospiceGuard] Rewriting cross-origin backend calls from ` +
          `${backendOrigin} to same-origin so cookies stay first-party. ` +
          `Configured REACT_APP_BACKEND_URL="${_CONFIGURED_BACKEND}", ` +
          `current origin="${window.location.origin}".`,
      );
      return backendOrigin;
    }
  } catch (_e) {
    /* malformed URL — no rewrite */
  }
  return null;
})();

function _rewriteBackendUrl(url) {
  if (!_CROSS_ORIGIN_BACKEND || typeof url !== "string") return url;
  if (url.startsWith(_CROSS_ORIGIN_BACKEND)) {
    return url.substring(_CROSS_ORIGIN_BACKEND.length) || "/";
  }
  return url;
}

// Axios request interceptor — rewrite baseURL + url if they target the
// cross-origin backend. Runs BEFORE the strip-Bearer-null interceptor.
axios.interceptors.request.use((config) => {
  if (_CROSS_ORIGIN_BACKEND) {
    if (typeof config.baseURL === "string" && config.baseURL.startsWith(_CROSS_ORIGIN_BACKEND)) {
      config.baseURL = config.baseURL.substring(_CROSS_ORIGIN_BACKEND.length) || "";
    }
    if (typeof config.url === "string") {
      config.url = _rewriteBackendUrl(config.url);
    }
  }
  return config;
});

// Deploy-blocker close-out (2026-07-21) — production auth race.
// ~80 pages/components still send `Authorization: Bearer ${token}` on every
// request. When `token` is null (page reload before AuthContext hydrates,
// or any downstream code that reads a stale null), they emit a literal
// `Bearer null` header. Backend `get_current_user` prefers the cookie when
// both are present, but on any race / cross-domain / SameSite miss where
// the cookie is momentarily absent, `jwt.decode("null", ...)` raises → 401
// "Invalid token" — the exact bug the user reported on production
// Compliant Rewrite, Ambient Scribe LCD types + templates, AI Assistant,
// document upload, and Eligibility. This interceptor strips the broken
// header BEFORE the request goes out, so those requests fall back cleanly
// to the cookie path via `withCredentials`.
axios.interceptors.request.use((config) => {
  const authHeader = config?.headers?.Authorization;
  if (
    typeof authHeader === "string" &&
    (authHeader === "Bearer null" ||
      authHeader === "Bearer undefined" ||
      authHeader === "Bearer " ||
      authHeader === "Bearer")
  ) {
    // Delete rather than overwrite — some routes explicitly reject an
    // empty Authorization header, and we want axios to fall back to the
    // cookie via `withCredentials` untouched.
    if (config.headers && typeof config.headers.delete === "function") {
      config.headers.delete("Authorization");
    } else if (config.headers) {
      delete config.headers.Authorization;
    }
  }
  return config;
});

// Patch window.fetch so every direct fetch() call sends the session cookie.
// We only inject `credentials: 'include'` when the caller hasn't set it — we
// never override an explicit choice. We ALSO rewrite cross-origin backend
// URLs to same-origin for the cookie fix documented above.
if (typeof window !== 'undefined' && window.fetch) {
  const _originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    if (init && init.credentials === undefined) {
      init = { ...init, credentials: 'include' };
    }
    if (_CROSS_ORIGIN_BACKEND) {
      if (typeof input === "string") {
        input = _rewriteBackendUrl(input);
      } else if (input && typeof input.url === "string" && input.url.startsWith(_CROSS_ORIGIN_BACKEND)) {
        // `input` is a Request object — clone with the rewritten URL.
        input = new Request(_rewriteBackendUrl(input.url), input);
      }
    }
    return _originalFetch(input, init);
  };
}

// Belt-and-suspenders: ensure ANY axios error rendered as `e.message` shows
// a readable string instead of `[object Object]`. This runs once at module
// load and rewrites `e.message` IN PLACE before any per-call handler sees it.
// formatApiError() is still preferred at call sites for richer formatting.
//
// 2026-02-25 P0 v5 → v8 — transient-401 auto-retry with escalating backoff
// ------------------------------------------------------------------------
// On production replica sets, session_store lookups can race with the
// backend session insert on the very first authenticated call after
// login. The backend has a JWT grace period as a first defense; this
// interceptor is the SECOND defense.
//
// v8: three retries with 500ms → 1500ms → 3000ms delays (total up to
// 5 seconds). Empirically, 30-40% of individual /me calls on production
// were failing before v4 grace period landed. With 3 retries at
// escalating backoff, the P(all 4 attempts fail) = 0.4^4 = 2.5%, and
// the total delay when all succeed by attempt 2-3 is still under 2 sec
// — well below any human perception threshold for "dashboard loaded".
//
// A REAL session expiry / revocation still 401's on ALL retries and the
// caller sees the error unchanged. A transient race is invisibly
// smoothed over. The `__retried401Attempts` marker prevents infinite
// loops.
const _RETRY_DELAYS_MS = [500, 1500, 3000];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config;
    const url = cfg?.url || '';

    // Skip retry for: the login endpoint itself (a 401 there is genuine
    // wrong-password); explicit logout calls; requests that have already
    // been retried MAX_ATTEMPTS times; any non-authenticated path.
    const attempts = cfg?.__retried401Attempts || 0;
    const isRetriableAuthMiss =
      status === 401 &&
      cfg &&
      attempts < _RETRY_DELAYS_MS.length &&
      !url.includes('/auth/login') &&
      !url.includes('/auth/logout') &&
      !url.includes('/auth/register');

    if (isRetriableAuthMiss) {
      try {
        const delay = _RETRY_DELAYS_MS[attempts];
        console.info(
          `[axios] transient 401 on ${url} → retry ${attempts + 1}/${_RETRY_DELAYS_MS.length} after ${delay}ms`,
        );
        cfg.__retried401Attempts = attempts + 1;
        // Preserve legacy marker so older consumers (or downstream
        // interceptors) can still detect that a retry happened.
        cfg.__retried401 = true;
        await new Promise((res) => setTimeout(res, delay));
        return await axios(cfg);
      } catch (retryErr) {
        // Retry also 401'd — check if we can keep retrying.
        const retryStatus = retryErr?.response?.status;
        const nextAttempts = retryErr?.config?.__retried401Attempts || attempts + 1;
        if (retryStatus === 401 && nextAttempts < _RETRY_DELAYS_MS.length) {
          // Recursion is handled by the interceptor re-firing on the
          // rejected retry — the outer catch here only runs when we've
          // exhausted attempts or the retry threw a non-response error.
        }
        error = retryErr;
      }
    }

    try {
      const friendly = formatApiError(error, error?.message || 'Request failed');
      // Don't shadow the original message if it's already a clean string
      if (typeof error?.message === 'string' && !error.message.includes('[object')) {
        error.friendlyMessage = friendly;
      } else {
        error.message = friendly;
        error.friendlyMessage = friendly;
      }
    } catch {
      /* never block error propagation */
    }
    return Promise.reject(error);
  },
);

// Demo read-only interceptor. The backend `DemoReadOnlyMiddleware` returns
// HTTP 403 with `{ demo_readonly: true, ... }` on any mutation attempted by
// a demo session. We surface that as a single friendly toast so callers do
// not need to add per-endpoint UI for it.
//
// Toast is only shown ONCE per 2s window to avoid spam when a page fires a
// batch of writes on mount (e.g., saving preferences on load).
let _lastDemoToastAt = 0;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error?.response?.status === 403 &&
      error?.response?.data?.demo_readonly === true
    ) {
      const now = Date.now();
      if (now - _lastDemoToastAt > 2000) {
        _lastDemoToastAt = now;
        // Dynamic import so the interceptor never blocks module load.
        import('sonner')
          .then(({ toast }) => {
            toast.info(
              error.response.data.detail ||
                'This action is not available in read-only demo mode.',
              {
                description: 'Sign up for a free trial to unlock full access.',
                duration: 6000,
              },
            );
          })
          .catch(() => {
            /* toast library unavailable — do nothing */
          });
      }
    }
    return Promise.reject(error);
  },
);

// Stale-JWT self-heal interceptor — REMOVED 2026-02-19 (v3).
//
// History: v1 was too permissive and bounced on any 401 → broke mount-time
// /auth/me. v2 tightened but still bounced under some conditions the user
// reported. Root call: this interceptor was SPECULATIVE — added to soften
// a "Invalid token" toast that turned out to be a stale production deploy,
// not a real code path. Removing the interceptor entirely restores the
// AuthProvider's clean 401 handling and eliminates the whole class of
// bounce regressions. If we truly need session-invalidation UX later, we
// build it INSIDE AuthProvider's `/auth/me` and `refreshUser` handlers,
// not as a global interceptor.

// Critical pages - loaded immediately (login flow)
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LandingPage from "./pages/LandingPage";
import PartnershipPage from "./pages/PartnershipPage";
import OrgLoginPage from "./pages/OrgLoginPage";
import DemoLandingPage from "./pages/DemoLandingPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";

// Lazy-loaded pages - loaded on demand (reduces initial bundle by ~70%)
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MyDayPage = lazy(() => import("./pages/MyDayPage"));
const MfaSetupPage = lazy(() => import("./pages/MfaSetupPage"));
const UploadPage = lazy(() => import("./pages/UploadPage"));
const EligibilityPage = lazy(() => import("./pages/EligibilityPage"));
const AuditResultPage = lazy(() => import("./pages/AuditResultPage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const CompliancePage = lazy(() => import("./pages/CompliancePage"));
const ComplianceDashboardPage = lazy(() => import("./pages/ComplianceDashboardPage"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage"));
const PendingStartersAdminPage = lazy(() => import("./pages/PendingStartersAdminPage"));
const DiagnosticsAdminPage = lazy(() => import("./pages/DiagnosticsAdminPage"));
const WhiteLabelDomainPage = lazy(() => import("./pages/admin/WhiteLabelDomainPage"));
const AmbientScribePage = lazy(() => import("./pages/AmbientScribePage"));
const VoiceNoteQuickCapture = lazy(() => import("./pages/VoiceNoteQuickCapture"));
const IdgAutoPrepPage = lazy(() => import("./pages/IdgAutoPrepPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const PartnershipInboxPage = lazy(() => import("./pages/PartnershipInboxPage"));
const AuditLogsPage = lazy(() => import("./pages/AuditLogsPage"));
const BatchProcessingPage = lazy(() => import("./pages/BatchProcessingPage"));
const ExecutiveDashboardPage = lazy(() => import("./pages/ExecutiveDashboardPage"));
const ComplianceCalendarPage = lazy(() => import("./pages/ComplianceCalendarPage"));
const TemplatesLibraryPage = lazy(() => import("./pages/TemplatesLibraryPage"));
const EMRIntegrationPage = lazy(() => import("./pages/EMRIntegrationPage"));
const AuditDefensePage = lazy(() => import("./pages/AuditDefensePage"));
const DefenseAnalyticsDashboard = lazy(() => import("./pages/DefenseAnalyticsDashboard"));
const ComplianceEnginePage = lazy(() => import("./pages/ComplianceEnginePage"));
const AdvancedCompliancePage = lazy(() => import("./pages/AdvancedCompliancePage"));
const RegulatoryFeedPage = lazy(() => import("./pages/RegulatoryFeedPage"));
const ReferralPortalPage = lazy(() => import("./pages/ReferralPortalPage"));
const CertificationPage = lazy(() => import("./pages/CertificationPage"));
const DomainSettingsPage = lazy(() => import("./pages/DomainSettingsPage"));
const CommandCenterPage = lazy(() => import("./pages/CommandCenterPage"));
const CompanyBrandingPage = lazy(() => import("./pages/CompanyBrandingPage"));
const HIPAACompliancePage = lazy(() => import("./pages/HIPAACompliancePage"));
const HIPAAComplianceProofPage = lazy(() => import("./pages/HIPAAComplianceProofPage"));
const RiskCockpitPage = lazy(() => import("./pages/RiskCockpitPage"));
const ROIDashboardPage = lazy(() => import("./pages/ROIDashboardPage"));
const CodingAssistantPage = lazy(() => import("./pages/CodingAssistantPage"));
const FieldModePage = lazy(() => import("./pages/FieldModePage"));
const ADRResponseBuilderPage = lazy(() => import("./pages/ADRResponseBuilderPage"));
const ComplianceReviewPage = lazy(() => import("./pages/ComplianceReviewPage"));
const EMRAutomationPage = lazy(() => import("./pages/EMRAutomationPage"));
const SSVIDashboard = lazy(() => import("./pages/ssvi/SSVIDashboard"));
const SSVIDataCenter = lazy(() => import("./pages/ssvi/SSVIDataCenter"));
const SSVIMeasureDetail = lazy(() => import("./pages/ssvi/SSVIMeasureDetail"));
const SSVIActionCenter = lazy(() => import("./pages/ssvi/SSVIActionCenter"));
const SSVINonHospiceReview = lazy(() => import("./pages/ssvi/SSVINonHospiceReview"));
const SurveyReadinessPage = lazy(() => import("./pages/SurveyReadinessPage"));
const NetworkOversight = lazy(() => import("./pages/NetworkOversight"));
import { AUTO_ROUTED_FEATURES } from "./config/featureRegistry";
const FeatureHubPage = lazy(() => import("./pages/FeatureHubPage"));

const PricingPage = lazy(() => import("./pages/PricingPage"));
const ChapPitchDeck = lazy(() => import("./pages/ChapPitchDeck"));
const PitchDeckPage = lazy(() => import("./pages/PitchDeckPage"));
const OnePagerHandout = lazy(() => import("./pages/OnePagerHandout"));
const ClinicalScribePage = lazy(() => import("./pages/ClinicalScribePage"));
const AmbientScribeHistoryPage = lazy(() => import("./pages/AmbientScribeHistoryPage"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const PHIAuditDashboard = lazy(() => import("./pages/PHIAuditDashboard"));
const ComplianceAlertsPage = lazy(() => import("./pages/ComplianceAlertsPage"));
const OrganizationSettingsPage = lazy(() => import("./pages/OrganizationSettingsPage"));
const ActivityTimelinePage = lazy(() => import("./pages/ActivityTimelinePage"));
const CTINarrativePage = lazy(() => import("./pages/CTINarrativePage"));
const F2FEncounterPage = lazy(() => import("./pages/F2FEncounterPage"));
const CTIHistoryPage = lazy(() => import("./pages/CTIHistoryPage"));
const PrognosisSignoffPage = lazy(() => import("./pages/PrognosisSignoffPage"));
const HOPEValidationPage = lazy(() => import("./pages/HOPEValidationPage"));
const OnePagerROIPage = lazy(() => import("./pages/OnePagerROIPage"));
const SuccessStoryPage = lazy(() => import("./pages/SuccessStoryPage"));
const CGSAuditorPage = lazy(() => import("./pages/CGSAuditorPage"));
const CGSAuditHistoryPage = lazy(() => import("./pages/CGSAuditHistoryPage"));
const VisitAuditPage = lazy(() => import("./pages/VisitAuditPage"));
const IDGMeetingPrepPage = lazy(() => import("./pages/IDGMeetingPrepPage"));
const PatientTrackerPage = lazy(() => import("./pages/PatientTrackerPage"));
const BulkNotesPage = lazy(() => import("./pages/BulkNotesPage"));
const QAReviewPage = lazy(() => import("./pages/QAReviewPage"));
const PayerIntelligencePage = lazy(() => import("./pages/PayerIntelligencePage"));
const ADRWorkflowPage = lazy(() => import("./pages/ADRWorkflowPage"));
const VoiceDiarizationPage = lazy(() => import("./pages/VoiceDiarizationPage"));
const TPEAuditPage = lazy(() => import("./pages/TPEAuditPage"));
const ComplianceCoachPage = lazy(() => import("./pages/ComplianceCoachPage"));
const SecurityDashboardPage = lazy(() => import("./pages/SecurityDashboardPage"));
const SystemStatusPage = lazy(() => import("./pages/SystemStatusPage"));
const TruthSafetyPage = lazy(() => import("./pages/TruthSafetyPage"));

import Layout from "./components/Layout";
import ConnectionStatus from "./components/ConnectionStatus";
import ServiceWorkerUpdatePrompt from "./components/ServiceWorkerUpdatePrompt";
import { OfflineProvider } from "./contexts/OfflineContext";

// Use relative URL for API to work on any domain (custom domain or preview)
// 2026-02-25 PRODUCTION COOKIE FIX
// ================================
// Previously we used the absolute `REACT_APP_BACKEND_URL` for every API
// call. That works when the frontend and backend live on the same origin
// (preview: hospice-guard-3.preview.emergentagent.com), but the moment a
// custom production domain (e.g. careflow-safety.emergent.host) points at
// the same app while REACT_APP_BACKEND_URL still says
// `https://goodwinguard.com`, EVERY XHR becomes cross-origin.
//
// Modern browsers (Chrome 130+, Safari, Firefox) BLOCK third-party
// cookies by default. Result: `Set-Cookie` from login is silently
// discarded, `/auth/me` returns 401 on the very next request, the
// dashboard fires "Failed to load dashboard data", and the user is
// bounced back to /login — the exact bug users reported.
//
// Fix: only use the absolute URL when it matches the current window
// origin. Otherwise fall back to a RELATIVE `/api` prefix, which the
// ingress routes to the same backend but keeps every cookie strictly
// first-party. Preview keeps its exact behaviour. `goodwinguard.com`
// users keep their exact behaviour. `careflow-safety.emergent.host`
// (and any future custom domain) starts working immediately.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
let _apiPrefix = "/api";
if (BACKEND_URL) {
  try {
    const _backendOrigin = new URL(BACKEND_URL).origin;
    const _currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
    if (_currentOrigin && _backendOrigin === _currentOrigin) {
      _apiPrefix = `${BACKEND_URL}/api`;
    }
    // else: cross-origin — keep relative `/api` to preserve first-party cookies
  } catch (_e) {
    // Malformed REACT_APP_BACKEND_URL — safest fallback is relative
    _apiPrefix = "/api";
  }
}
export const API = _apiPrefix;

// Configure axios with retry logic for high availability
axios.defaults.timeout = 30000; // 30 second timeout

// HIPAA: any API call refused because the account has no authenticator yet → enrollment screen.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403 && error.response?.data?.detail === "MFA_ENROLLMENT_REQUIRED"
        && window.location.pathname !== "/mfa-setup") {
      const last = Number(sessionStorage.getItem("hg_mfa_redirect_at") || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem("hg_mfa_redirect_at", String(Date.now()));
        window.location.assign("/mfa-setup");
      }
    }
    return Promise.reject(error);
  }
);

// Retry interceptor for transient failures
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Don't retry if we've already retried or if it's a 4xx error
    if (config._retry || (error.response?.status >= 400 && error.response?.status < 500)) {
      return Promise.reject(error);
    }
    
    // Retry on network errors or 5xx errors (server issues)
    if (!error.response || error.response.status >= 500) {
      config._retry = true;
      config._retryCount = (config._retryCount || 0) + 1;
      
      // Only retry up to 2 times
      if (config._retryCount <= 2) {
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * config._retryCount));
        return axios(config);
      }
    }
    
    return Promise.reject(error);
  }
);

// Truth Engine warn mode: a flagged save asks the clinician for a reason, then resends with it.
installTruthAckInterceptor();

// Page loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="flex flex-col items-center gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Wrap lazy components with Suspense + a chunk-error boundary so a stale
// route chunk after a deploy recovers with a reload instead of a blank page.
const LazyPage = ({ children }) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  </ChunkErrorBoundary>
);

// Redirect component for /app/audit/:id -> /app/result/:id
const AuditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/app/result/${id}`} replace />;
};

// Theme context
const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved || "system";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    const root = document.documentElement;
    
    if (theme === "system") {
      const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      root.classList.toggle("dark", isDark);
    } else {
      root.classList.toggle("dark", theme === "dark");
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Auth context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Session timeout configuration (HIPAA compliant)
const SESSION_WARNING_TIME = 13 * 60 * 1000; // 13 minutes
const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes
const MAX_SESSION_DURATION = 8 * 60 * 60 * 1000; // 8 hours

const AuthProvider = ({ children }) => {
  // P3 Cookie Migration + belt-and-suspenders (2026-07-21).
  //
  // The HttpOnly `session_token` cookie is the primary auth mechanism —
  // set by the backend on login, sent by the browser on every same-origin
  // request via `axios.defaults.withCredentials = true`. XSS can never
  // read it. This is the design intent.
  //
  // BUT: ~80 page/component files still send `Authorization: Bearer
  // ${token}` on every request. When they read `token` from context and
  // it's null (previous migration state), they send literal `Bearer
  // null`, which the backend's `get_current_user` used to accept ONLY
  // when the cookie was present. Any cross-domain deploy scenario,
  // SameSite=Lax cookie-miss, or session-store race then flips to the
  // Bearer path with `raw_token = "null"` → jwt.decode raises → 401
  // "Invalid token" — the exact bug the user reported on production
  // Compliant Rewrite + Ambient Scribe + AI Assistant + Eligibility.
  //
  // Fix: keep the token in a JS state variable (in-memory only — never
  // localStorage). Populated on login from the response body. Cleared
  // on logout. Lost on page reload, at which point `/auth/me` (via the
  // cookie) rehydrates `authenticated` + `user` and pages that need
  // `token` for their Bearer header will get a page-reload no-token
  // interval where those requests fall back to the cookie path
  // (server.py:4130-4134 accepts either).
  //
  // Security note: this is NOT the same as the pre-migration
  // localStorage token. The runtime attack surface is essentially
  // equivalent to reading `document.cookie` on a legitimately-scoped
  // cookie — the JS-accessible token IS a target for a runtime XSS,
  // but it dies on refresh and never persists to disk. The cookie
  // remains the authoritative auth mechanism.
  const [token, setToken] = useState(null);
  const [authenticated, setAuthenticated] = useState(true); // optimistic — /me will clear
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [sessionStart, setSessionStart] = useState(Date.now());

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      setLastActivity(Date.now());
      setShowSessionWarning(false);
    };

    // Track various user activities
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, []);

  // Session timeout logic (HIPAA § 164.312(a)(2)(iii))
  useEffect(() => {
    if (!authenticated) return;

    const checkSession = () => {
      const now = Date.now();
      const idleTime = now - lastActivity;
      const totalSessionTime = now - sessionStart;

      // Check for max session duration
      if (totalSessionTime >= MAX_SESSION_DURATION) {
        logout();
        return;
      }

      // Check for idle timeout
      if (idleTime >= SESSION_TIMEOUT) {
        logout();
        return;
      }

      // Show warning before timeout
      if (idleTime >= SESSION_WARNING_TIME && !showSessionWarning) {
        setShowSessionWarning(true);
        setSessionTimeRemaining(Math.ceil((SESSION_TIMEOUT - idleTime) / 1000));
      }

      // Update countdown
      if (showSessionWarning) {
        const remaining = Math.ceil((SESSION_TIMEOUT - idleTime) / 1000);
        setSessionTimeRemaining(Math.max(0, remaining));
        if (remaining <= 0) {
          logout();
        }
      }
    };

    const interval = setInterval(checkSession, 1000);
    return () => clearInterval(interval);
  }, [authenticated, lastActivity, sessionStart, showSessionWarning]);

  const extendSession = () => {
    setLastActivity(Date.now());
    setShowSessionWarning(false);
  };

  useEffect(() => {
    const fetchUser = async () => {
      // Cookie is HttpOnly, so we can't check for it in JS — we probe /me.
      // If cookie is missing/expired, /me returns 401 and we clear state.
      //
      // 2026-02-25 P0 v5 — client-side transient-401 retry.
      // -----------------------------------------------------
      // On production replica sets we sometimes see the very first
      // `/auth/me` return 401 while the backend session write is still
      // replicating (see backend session_store.py + auth.py grace period
      // for the server-side fix). Even if the browser is 100% correctly
      // sending the session cookie, MongoDB read consistency can beat us
      // by a few hundred ms.
      //
      // Client-side retry: on 401 from /auth/me, wait 500ms then retry
      // ONCE. If that also 401's, THEN we bounce to /login. This closes
      // the last remaining refresh-bounce hole without requiring any
      // backend cooperation. Cost is one extra request only when the
      // backend was going to reject us anyway — invisible in the
      // happy path.
      const _hitMeOnce = () => axios.get(`${API}/auth/me`);
      const _sleep = (ms) => new Promise((res) => setTimeout(res, ms));
      try {
        let response;
        try {
          response = await _hitMeOnce();
        } catch (firstErr) {
          const s1 = firstErr.response?.status;
          if (s1 === 401 || s1 === 403) {
            // Transient race with backend session replication?
            console.info("[auth] /me returned", s1, "— retrying once after 500ms");
            await _sleep(500);
            response = await _hitMeOnce();
          } else {
            throw firstErr;
          }
        }
        setUser(response.data);
        setAuthenticated(true);
        setSessionStart(Date.now());
      } catch (error) {
        // 401/403 = no valid session cookie; anything else = network issue
        if (error.response?.status === 401 || error.response?.status === 403) {
          setAuthenticated(false);
          setUser(null);
        } else {
          console.warn("Network error fetching user; will retry on next mount");
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const login = async (email, password, totpCode = null) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password, ...(totpCode ? { totp_code: totpCode } : {}) });
      const { token: newToken, user: userData, must_change_password, requires_2fa, requires_mfa_setup, backup_code_used } = response.data;

      // 2FA account: the password was right; ask for the authenticator code next.
      if (requires_2fa) {
        return { success: true, requires2FA: true };
      }

      // must_change_password: cookie IS set by backend, but frontend routes
      // to ChangePasswordPage immediately. It uses the cookie for the call.
      if (must_change_password) {
        setToken(newToken);  // needed by ChangePasswordPage's Bearer header
        return {
          success: true,
          must_change_password: true,
          token: newToken,  // kept for legacy consumers that still read this value
          user: userData
        };
      }

      // Cookie was set server-side; ALSO store token in-memory so the ~80
      // pages that still send `Authorization: Bearer ${token}` get a real
      // value instead of `Bearer null`. See AuthProvider docstring.
      setToken(newToken);
      setAuthenticated(true);
      setUser(userData);
      setLastActivity(Date.now());
      setSessionStart(Date.now());
      return { success: true, user: userData, requiresMfaSetup: !!requires_mfa_setup, backupCodeUsed: !!backup_code_used };
    } catch (error) {
      return {
        success: false,
        error: formatApiError(error, "Login failed"),
      };
    }
  };

  const logout = async () => {
    // Best-effort: revoke the session server-side and clear the cookie.
    // Even if this fails (network), we still purge client state.
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // no-op
    }
    setToken(null);
    setAuthenticated(false);
    setUser(null);
    setShowSessionWarning(false);
    await clearBrowserPhi();
  };

  const refreshUser = async () => {
    if (authenticated) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error("Failed to refresh user:", error);
        return null;
      }
    }
    return null;
  };

  // Compute role-based flags
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";
  const isPhysician = user?.role === "physician";
  const isNetworkAdmin = user?.role === "network_admin";

  return (
    <AuthContext.Provider value={{
      // Real JWT while the user is logged in — populated on login(),
      // cleared on logout(), lost on page reload (in-memory only). The
      // ~80 pages that still send `Authorization: Bearer ${token}` now
      // get a real value instead of `Bearer null`. HttpOnly cookie
      // remains the primary auth mechanism for same-origin same-tab.
      // See AuthProvider docstring for the full rationale.
      token,
      authenticated,
      user,
      login,
      logout,
      loading,
      refreshUser,
      isAdmin,
      isSuperAdmin,
      isPhysician,
      isNetworkAdmin
    }}>
      {children}
      <SessionWarningDialog
        open={showSessionWarning}
        timeRemaining={sessionTimeRemaining}
        onExtend={extendSession}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
};

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { authenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!authenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // HIPAA: accounts that can see patients must enroll an authenticator first.
  if (user?.mfa_setup_required) {
    return <Navigate to="/mfa-setup" replace />;
  }

  // Wait for user data to load before checking roles
  // This prevents premature redirects when user data is still being fetched
  if (allowedRoles && !user) {
    return <PageLoader />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <OfflineProvider>
            <FavoritesProvider>
              <DomainProvider>
                <ConnectionStatus />
                <PWAInstallPrompt />
                <Routes>
                  {/* Public routes - not lazy loaded for fast initial experience */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/demo" element={<DemoLandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/mfa-setup" element={<LazyPage><MfaSetupPage /></LazyPage>} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/partnership" element={<PartnershipPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/org/:domain" element={<OrgLoginPage />} />
                  
                  {/* Redirect old /dashboard to /app/dashboard for backwards compatibility */}
                  <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
                  
                  {/* Public marketing pages - lazy loaded */}
                  <Route path="/roi" element={<LazyPage><OnePagerROIPage /></LazyPage>} />
                  <Route path="/success-story" element={<LazyPage><SuccessStoryPage /></LazyPage>} />
                  <Route path="/pricing" element={<LazyPage><PricingPage /></LazyPage>} />
                  <Route path="/pitch/chap" element={<LazyPage><ChapPitchDeck /></LazyPage>} />
                  <Route path="/pitch" element={<LazyPage><PitchDeckPage /></LazyPage>} />
                  <Route path="/handout" element={<LazyPage><OnePagerHandout /></LazyPage>} />

                  {/* Protected app routes - all lazy loaded */}
                  <Route
                    path="/app"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="my-day" replace />} />
                    <Route path="my-day" element={<LazyPage><MyDayPage /></LazyPage>} />
                    <Route path="dashboard" element={<LazyPage><DashboardPage /></LazyPage>} />
                    <Route path="upload" element={<LazyPage><UploadPage /></LazyPage>} />
                  <Route path="eligibility" element={<LazyPage><EligibilityPage /></LazyPage>} />
                  <Route path="result/:id" element={<LazyPage><AuditResultPage /></LazyPage>} />
                  {/* Redirect /app/audit/:id to /app/result/:id for backward compatibility */}
                  <Route path="audit/:id" element={<AuditRedirect />} />
                  <Route path="history" element={<LazyPage><HistoryPage /></LazyPage>} />
                  <Route path="settings" element={<LazyPage><SettingsPage /></LazyPage>} />
                  <Route path="insights" element={<LazyPage><InsightsPage /></LazyPage>} />
                  <Route path="compliance" element={<LazyPage><CompliancePage /></LazyPage>} />
                  <Route path="compliance-dashboard" element={<LazyPage><ComplianceDashboardPage /></LazyPage>} />
                  <Route path="ambient-scribe" element={<LazyPage><AmbientScribePage /></LazyPage>} />
                  <Route path="voice-note" element={<LazyPage><VoiceNoteQuickCapture /></LazyPage>} />
                  <Route path="idg-auto-prep" element={<LazyPage><IdgAutoPrepPage /></LazyPage>} />
                  <Route path="clinical-scribe" element={<LazyPage><ClinicalScribePage /></LazyPage>} />
                  <Route path="scribe-history" element={<LazyPage><AmbientScribeHistoryPage /></LazyPage>} />
                  <Route path="batch-processing" element={<LazyPage><BatchProcessingPage /></LazyPage>} />
                  <Route path="executive" element={<LazyPage><ExecutiveDashboardPage /></LazyPage>} />
                  <Route path="calendar" element={<LazyPage><ComplianceCalendarPage /></LazyPage>} />
                  <Route path="templates" element={<LazyPage><TemplatesLibraryPage /></LazyPage>} />
                  <Route path="emr" element={<LazyPage><EMRIntegrationPage /></LazyPage>} />
                  <Route path="defense" element={<LazyPage><AuditDefensePage /></LazyPage>} />
                  <Route path="defense-analytics" element={<LazyPage><DefenseAnalyticsDashboard /></LazyPage>} />
                  <Route path="compliance-engine" element={<LazyPage><ComplianceEnginePage /></LazyPage>} />
                  <Route path="advanced-compliance" element={<LazyPage><AdvancedCompliancePage /></LazyPage>} />
                  <Route path="regulatory-feed" element={<LazyPage><RegulatoryFeedPage /></LazyPage>} />
                  <Route path="referrals" element={<LazyPage><ReferralPortalPage /></LazyPage>} />
                  <Route path="certification" element={<LazyPage><CertificationPage /></LazyPage>} />
                  <Route path="command-center" element={<LazyPage><CommandCenterPage /></LazyPage>} />
                  <Route path="hipaa" element={<LazyPage><HIPAACompliancePage /></LazyPage>} />
                  <Route path="hipaa-proof" element={<LazyPage><HIPAAComplianceProofPage /></LazyPage>} />
                  <Route path="risk-cockpit" element={<LazyPage><RiskCockpitPage /></LazyPage>} />
                  <Route path="roi" element={<LazyPage><ROIDashboardPage /></LazyPage>} />
                  <Route path="coding-assistant" element={<LazyPage><CodingAssistantPage /></LazyPage>} />
                  <Route path="field-mode" element={<LazyPage><FieldModePage /></LazyPage>} />
                  <Route path="adr-response-builder" element={<LazyPage><ADRResponseBuilderPage /></LazyPage>} />
                  <Route path="compliance-review" element={<LazyPage><ComplianceReviewPage /></LazyPage>} />
                  <Route path="feature-hub" element={<LazyPage><FeatureHubPage /></LazyPage>} />
                  <Route path="phi-audit" element={<LazyPage><PHIAuditDashboard /></LazyPage>} />
                  <Route path="compliance-alerts" element={<LazyPage><ComplianceAlertsPage /></LazyPage>} />
                  <Route path="tools" element={<LazyPage><ToolsPage /></LazyPage>} />
                  <Route path="cti" element={<LazyPage><CTINarrativePage /></LazyPage>} />
                  <Route path="f2f" element={<LazyPage><F2FEncounterPage /></LazyPage>} />
                  <Route path="cti-history" element={<LazyPage><CTIHistoryPage /></LazyPage>} />
                  <Route path="prognosis-signoff" element={<LazyPage><PrognosisSignoffPage /></LazyPage>} />
                  <Route path="hope" element={<LazyPage><HOPEValidationPage /></LazyPage>} />
                  <Route path="cgs-auditor" element={<LazyPage><CGSAuditorPage /></LazyPage>} />
                  <Route path="cgs-audit-history" element={<LazyPage><CGSAuditHistoryPage /></LazyPage>} />
                  <Route path="visit-audit" element={<LazyPage><VisitAuditPage /></LazyPage>} />
                  <Route path="idg-meeting-prep" element={<LazyPage><IDGMeetingPrepPage /></LazyPage>} />
                  <Route path="emr-automation" element={<LazyPage><EMRAutomationPage /></LazyPage>} />
                  <Route path="ssvi/data" element={
                    <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance", "clinical_manager"]}>
                      <LazyPage><SSVIDataCenter /></LazyPage>
                    </ProtectedRoute>
                  } />
                  <Route path="ssvi/measure/:id" element={
                    <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance", "clinical_manager"]}>
                      <LazyPage><SSVIMeasureDetail /></LazyPage>
                    </ProtectedRoute>
                  } />
                  <Route path="ssvi/action-center" element={
                    <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance", "clinical_manager"]}>
                      <LazyPage><SSVIActionCenter /></LazyPage>
                    </ProtectedRoute>
                  } />
                  <Route path="ssvi/non-hospice" element={
                    <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance", "clinical_manager"]}>
                      <LazyPage><SSVINonHospiceReview /></LazyPage>
                    </ProtectedRoute>
                  } />
                  <Route path="patient-tracker" element={<LazyPage><PatientTrackerPage /></LazyPage>} />
                  <Route path="bulk-notes" element={<LazyPage><BulkNotesPage /></LazyPage>} />
                  <Route path="qa-review" element={<LazyPage><QAReviewPage /></LazyPage>} />
                  <Route path="payer-intelligence" element={<LazyPage><PayerIntelligencePage /></LazyPage>} />
                  <Route path="adr-workflow" element={<LazyPage><ADRWorkflowPage /></LazyPage>} />
                  <Route path="tpe-audit" element={<LazyPage><TPEAuditPage /></LazyPage>} />
                  <Route path="compliance-coach" element={<LazyPage><ComplianceCoachPage /></LazyPage>} />
                  <Route path="voice-diarization" element={<LazyPage><VoiceDiarizationPage /></LazyPage>} />
                  <Route path="activity" element={<LazyPage><ActivityTimelinePage /></LazyPage>} />
                  
                  {/* Route aliases for navigation consistency */}
                  <Route path="audit-defense" element={<LazyPage><AuditDefensePage /></LazyPage>} />
                  <Route path="batch" element={<Navigate to="/app/batch-processing" replace />} />
                  <Route path="cti-narrative" element={<LazyPage><CTINarrativePage /></LazyPage>} />
                  <Route path="f2f-encounter" element={<LazyPage><F2FEncounterPage /></LazyPage>} />
                  <Route path="hope-validation" element={<LazyPage><HOPEValidationPage /></LazyPage>} />
                  <Route path="scribe" element={<LazyPage><AmbientScribePage /></LazyPage>} />
                  <Route path="hipaa-compliance" element={<LazyPage><HIPAACompliancePage /></LazyPage>} />
                  <Route path="emr-integration" element={<LazyPage><EMRIntegrationPage /></LazyPage>} />
                  <Route path="compliance-calendar" element={<LazyPage><ComplianceCalendarPage /></LazyPage>} />
                  <Route path="domains" element={<Navigate to="/app/domain-settings" replace />} />
                  <Route path="onepager-roi" element={<LazyPage><OnePagerROIPage /></LazyPage>} />

                  {/* Auto-mounted feature routes — single source of truth: config/featureRegistry */}
                  {AUTO_ROUTED_FEATURES.map((f) => {
                    const C = f.component;
                    const inner = <LazyPage><C /></LazyPage>;
                    return (
                      <Route
                        key={f.id}
                        path={f.routePath}
                        element={f.roles ? <ProtectedRoute allowedRoles={f.roles}>{inner}</ProtectedRoute> : inner}
                      />
                    );
                  })}

                  {/* Admin routes */}
                  <Route
                    path="admin"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><AdminDashboard /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/security"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance"]}>
                        <LazyPage><SecurityDashboardPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/system-status"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance"]}>
                        <LazyPage><SystemStatusPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/truth-safety"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin", "compliance"]}>
                        <LazyPage><TruthSafetyPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><UserManagementPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/pending-starters"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><PendingStartersAdminPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/diagnostics"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><DiagnosticsAdminPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin/white-label"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><WhiteLabelDomainPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="audit-logs"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><AuditLogsPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="domain-settings"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><DomainSettingsPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="branding"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><CompanyBrandingPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="organization"
                    element={
                      <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
                        <LazyPage><OrganizationSettingsPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />

                  {/* Super Admin routes */}
                  <Route
                    path="super-admin"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <LazyPage><SuperAdminPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="partnership-inbox"
                    element={
                      <ProtectedRoute allowedRoles={["super_admin"]}>
                        <LazyPage><PartnershipInboxPage /></LazyPage>
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster richColors position="top-right" />
              <TruthAckDialog />
              <ServiceWorkerUpdatePrompt />
            </DomainProvider>
          </FavoritesProvider>
        </OfflineProvider>
      </AuthProvider>
    </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
